import { useToggle } from "@/hooks/use-toggle";

import Tabs from "@imminently/decisively-ui/Tabs";
import { ClickAwayListener, Typography, styled } from "@mui/material";
import cn from "clsx";
import React from "react";
import { useGraphVisualisation } from "../../GraphVisualisation/hooks/useGraphVisualisation";
import type { GraphColorScheme } from "../../GraphVisualisation/providers/RuleGraphProvider";

import stl from "./GraphPallet.module.scss";
import Icon from "@imminently/decisively-ui/Icon";

interface GraphPalletProps { }

/**
 * React Flow
 * @param props
 * @returns
 */
const GraphPallet = (props: GraphPalletProps) => {
  const outOfContextDefaults = {
    colorScheme: "by-type",
    setColorScheme: (colorScheme: GraphColorScheme) => { },
    debug: false,
    entityColorMapping: [],
  };
  const { colorScheme, setColorScheme, debug, entityColorMapping } = useGraphVisualisation(outOfContextDefaults);

  const isExpanded = useToggle(false);
  const tabs: { value: GraphColorScheme; label: string; ifDebug: boolean[] }[] = [
    { value: "by-value", label: "By Value", ifDebug: [true] },
    { value: "by-type", label: "By Type", ifDebug: [false] },
    { value: "by-entity", label: "By Entity", ifDebug: [true, false] },
  ];
  const visibleTabs = tabs.filter((it) => it.ifDebug.some((it2) => it2 === debug));

  React.useEffect(() => {
    if (!debug && colorScheme === "by-value") {
      setColorScheme("by-type");
    } else if (!visibleTabs.some((tab) => tab.value === colorScheme)) {
      setColorScheme(visibleTabs[0].value);
    }
  }, [debug, colorScheme, visibleTabs, setColorScheme]);

  // -- render

  const renderToggleButton = () => {
    return (
      <div
        className={stl.container_btn}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          isExpanded.toggle();
        }}
      >
        <div className={stl.svg_icon}>
          <Icon
            name="palette"
            style={{
              fill: isExpanded.value ? "#70F058" : "#000000",
            }}
          />
        </div>
        {/* <div
          className={stl.colored_circle}
        /> */}
      </div>
    );
  };

  const renderSwatchCard = (key: number, color: string, label: string) => {
    const backgroundColor = (() => {
      if (color.startsWith("--")) {
        return `var(${color})`;
      }
      return color;
    })();

    return (
      <div
        key={key}
        className={stl.swatch_card}
      >
        <div
          className={stl.swatch}
          style={{
            backgroundColor,
          }}
        />
        <div className={stl.swatch_label}>
          <Typography variant="caption">{label}</Typography>
        </div>
      </div>
    );
  };

  const renderPalletByType = () => {
    return (
      <>
        {renderSwatchCard(1, "--input-bg-color", "Source/ Input")}
        {renderSwatchCard(2, "--intermediate--bg-color", "Intermediate")}
        {renderSwatchCard(3, "--goal-bg-color", "Goal")}
      </>
    );
  };

  const renderPalletByValue = () => {
    return (
      <>
        {renderSwatchCard(1, "--value-color-unknown", "Unknown")}
        {renderSwatchCard(2, "--value-color-uncertain", "Uncertain")}
        {renderSwatchCard(3, "--value-color-falsy", "Falsey")}
        {renderSwatchCard(4, "--value-color-default", "Truthy")}
      </>
    );
  };

  const renderPalletByEntity = () => {
    const entitySwatches = entityColorMapping.map((mapping, idx) =>
      renderSwatchCard(idx, mapping.colorKey, mapping.entity),
    );

    return <>{entitySwatches}</>;
  };

  const renderPalletSwatches = () => {
    switch (colorScheme) {
      case "by-type":
        return renderPalletByType();
      case "by-value":
        return renderPalletByValue();
      case "by-entity":
        return renderPalletByEntity();
      default:
        return null;
    }

    return null;
  };

  const renderPallet = () => {
    return (
      <ClickAwayListener
        mouseEvent={isExpanded.value ? "onClick" : false}
        touchEvent={isExpanded.value ? "onTouchStart" : false}
        onClickAway={isExpanded.forceFalse}
      >
        <div className={cn(stl.container_pallet, { [stl.expanded]: isExpanded.value })}>
          <div className={cn(stl.col_lhs, { [stl.hidden]: false })}>
            <Tabs
              value={colorScheme}
              onValueChange={(newValue) => setColorScheme(newValue as GraphColorScheme)}
            >
              <Tabs.List className="flex flex-col border-b-0">
                {visibleTabs.map((tab, idx) => (
                  <Tabs.Trigger
                    key={idx}
                    value={tab.value}
                    className="justify-start text-left min-h-0 py-1.5 px-0 data-[state=active]:font-medium"
                  >
                    {tab.label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
            </Tabs>
          </div>
          <div className={stl.col_rhs}>{renderPalletSwatches()}</div>
        </div>
      </ClickAwayListener>
    );
  };

  return (
    <div>
      {renderToggleButton()}
      {renderPallet()}
    </div>
  );
};

export default GraphPallet;
